// src/components/inventory/InventoryStockEntryModal.tsx
// Codice già conosciuto: nome, categoria e prezzo li ha già, quindi si dice
// solo quanti pezzi entrano e la giacenza sale.
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, PackagePlus, Loader2, Plus } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import QuantityInput from '@/components/ui/QuantityInput';
import { useInventory } from '@/contexts/InventoryContext';
import { InventoryProduct } from '@/services/inventory/inventoryService';

const labelCls = 'block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2';

interface InventoryStockEntryModalProps {
    isOpen: boolean;
    onClose: () => void;
    product: InventoryProduct | null;
    onRegistered?: () => void;
}

const InventoryStockEntryModal: React.FC<InventoryStockEntryModalProps> = ({
    isOpen,
    onClose,
    product,
    onRegistered,
}) => {
    const { t } = useTranslation();
    const { addMovement } = useInventory();

    const [quantity, setQuantity] = useState(1);
    const [isSaving, setIsSaving] = useState(false);

    // Il prodotto sparisce prima che il modale finisca di chiudersi: teniamo l'ultimo.
    const lastProduct = useRef<InventoryProduct | null>(null);
    if (product) lastProduct.current = product;
    const shown = product ?? lastProduct.current;

    useEffect(() => {
        if (!isOpen) return;
        setQuantity(1);
        setIsSaving(false);
    }, [isOpen, product?.id]);

    const handleRegister = async () => {
        if (!product) return;
        setIsSaving(true);
        const res = await addMovement({ product_id: product.id, type: 'in', quantity });
        setIsSaving(false);
        if (res.success) {
            toast.success(
                t('inventory.scan.entry_saved', '{{qty}} × {{name}} entrati a magazzino', {
                    qty: quantity,
                    name: product.name,
                })
            );
            onClose();
            onRegistered?.();
        } else {
            toast.error(res.error || t('common.error'));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-lg"
            headerClassName="bg-white border-b border-gray-100"
            closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
            headerContent={
                <div className="flex items-center gap-3.5 pr-10">
                    <div className="w-12 h-12 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center flex-shrink-0">
                        <PackagePlus size={22} />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 truncate">
                            {shown?.name ?? t('inventory.scan.entry', 'Ingresso merce')}
                        </h2>
                        <p className="text-sm text-gray-400 truncate">
                            {shown?.category}
                            {shown?.barcode ? ` · ${shown.barcode}` : ''}
                        </p>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
                    <div className="w-9 h-9 rounded-xl bg-white/70 text-emerald-600 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 size={16} />
                    </div>
                    <div className="min-w-0 space-y-1 pt-0.5">
                        <p className="text-sm font-bold text-emerald-700 leading-snug">
                            {t('inventory.scan.recognized', 'Prodotto riconosciuto — in ingresso')}
                        </p>
                        <p className="text-xs text-emerald-700/80">
                            {t('inventory.current_stock', 'giacenza attuale {{n}}', { n: shown?.stock ?? 0 })}
                        </p>
                    </div>
                </div>

                <div className="sm:max-w-[220px]">
                    <label className={labelCls}>{t('inventory.scan.qty_in', 'Quantità in ingresso')}</label>
                    <QuantityInput value={quantity} onChange={setQuantity} disabled={isSaving} />
                </div>

                <div className="flex justify-end pt-5 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={handleRegister}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-6 py-3 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm shadow-sm shadow-clip-200 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                        {t('common.add', 'Aggiungi')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default InventoryStockEntryModal;
